import { type OutputUnit, type Schema, Validator } from '@cfworker/json-schema';

import {
  createErrorHandler,
  CustomValidator,
  ErrorSchema,
  ErrorTransformer,
  FormContextType,
  GenericObjectType,
  getDefaultFormState,
  RJSFValidationError,
  StrictRJSFSchema,
  toErrorSchema,
  UiSchema,
  unwrapErrorHandler,
  ValidationData,
  validationDataMerge,
  ValidatorType,
  withIdRefPrefix,
} from '@rjsf/utils';

const MISSING_REQUIRED_PROPERTY = 'does not have required property';
const DRAFT = '7';
const HASH_OR_HASH_SLASH_REGEX = /^#\/?/;

class CfWorkerFormValidator<T, S extends StrictRJSFSchema, F extends FormContextType = GenericObjectType>
  implements ValidatorType<T, S, F>
{
  validateFormData(
    formData: T | undefined,
    schema: S,
    customValidate?: CustomValidator<T, S, F>,
    transformErrors?: ErrorTransformer<T, S, F>,
    uiSchema?: UiSchema<T, S, F>,
  ): ValidationData<unknown> {
    const rawErrors = this.rawValidation<OutputUnit>(schema, formData);

    const { validationError } = rawErrors;
    let errors = this.transformValidationErrors(rawErrors.errors);

    const noMetaSchema = validationError?.message?.includes('no schema with key or ref ');

    if (noMetaSchema) {
      errors = [...errors, { stack: validationError.message }];
    }

    if (typeof transformErrors === 'function') {
      errors = transformErrors(errors, uiSchema);
    }

    let errorSchema = toErrorSchema<T>(errors);

    if (noMetaSchema) {
      errorSchema = {
        ...errorSchema,
        $schema: {
          __errors: [validationError.message],
        },
      };
    }

    if (typeof customValidate !== 'function') {
      return { errors, errorSchema };
    }

    // Include form data with undefined values, which is required for custom validation.
    const defaultFormData = getDefaultFormState<T, S, F>(this, schema, formData, schema, true) as T;

    const errorHandler = customValidate(defaultFormData, createErrorHandler<T>(defaultFormData), uiSchema);
    const userErrorSchema = unwrapErrorHandler<T>(errorHandler);
    return validationDataMerge<T>({ errors, errorSchema }, userErrorSchema);
  }

  private transformValidationErrors(errors: OutputUnit[] = []): RJSFValidationError[] {
    return errors
      .filter((error) => error.instanceLocation !== '#' || error.keyword === 'required')
      .map((outputUnit) => {
        const { keyword, keywordLocation, instanceLocation, error } = outputUnit;

        let property = '';

        switch (keyword) {
          case 'required': {
            const matches = error.includes(MISSING_REQUIRED_PROPERTY);

            property = `${instanceLocation.replace(HASH_OR_HASH_SLASH_REGEX, '')}.${matches?.[1] ?? ''}`;
            break;
          }

          case 'type': {
            property = instanceLocation.replace(HASH_OR_HASH_SLASH_REGEX, '');
            break;
          }

          default: {
            property = keywordLocation.replace('#/properties/', '').replace(`/${keyword}`, '');
          }
        }

        // Put data in expected format
        return {
          name: keyword,
          property,
          message: error,
          stack: `${keywordLocation} ${error}`.trim(),
          schemaPath: instanceLocation,
        };
      });
  }

  isValid(schema: S, formData: T | undefined): boolean {
    // validate the schema itself
    try {
      if (!new Validator(schema as Schema, DRAFT).validate(schema).valid) {
        return false;
      }
    } catch {
      return false;
    }

    // validate the formData against the schema
    return new Validator(withIdRefPrefix(schema) as Schema, DRAFT, true).validate(formData).valid;
  }

  rawValidation<Result = any>(schema: S, formData?: T): { errors?: Result[]; validationError?: Error } {
    try {
      const validator = new Validator(schema as Schema, DRAFT, false);
      return {
        errors: validator.validate(formData).errors as unknown as Result[],
      };
    } catch (error) {
      if (error instanceof Error) {
        return { validationError: error };
      }

      return { validationError: new Error(error) };
    }
  }

  // deprecated and unused, but still needed for implementing the interface.
  toErrorList(errorSchema?: ErrorSchema<T>, fieldPath?: string[]): RJSFValidationError[] {
    return [];
  }
  reset?: () => void;
}

const validator = new CfWorkerFormValidator();

export default validator;
